import type { AnySceneObjectT } from "tweenly"
import { sortObjects } from "tweenly"

/**
 * Drag & drop in the objects tree — pure rules, no DOM, no Redux.
 *
 * A row is dropped BEFORE or AFTER another row (it becomes that row's sibling) or INSIDE it
 * (it becomes its last child), the way layers are rearranged in Figma.
 */

export type ObjectsByIdT = Record<string, AnySceneObjectT>

export type DropPlacementT = "before" | "after" | "inside"

/**
 * The object types that can hold children — for "add child" as well. An image, image sequence,
 * ticker or line holds none: the canvas does not even render children of an image.
 */
export const PARENT_OBJECT_TYPES = ["item", "ellipse"]

/** Share of a row's height, at its top and bottom, that drops before / after it. */
const EDGE_ZONE = 0.25

/** Every object in the order the tree lists them: roots by index, then down each branch. */
export const treeOrder = (objectsById: ObjectsByIdT): string[] => {
    const order: string[] = []
    const visit = (object: AnySceneObjectT | undefined) => {
        if (!object) return
        order.push(object.id)
        object.childIds.forEach((childId) => visit(objectsById[childId]))
    }
    sortObjects(Object.values(objectsById).filter((object) => object.parentId === null)).forEach(
        visit
    )
    return order
}

/** Whether `id` sits anywhere inside `ancestorId`. */
export const isDescendantOf = (objectsById: ObjectsByIdT, id: string, ancestorId: string) => {
    for (
        let current = objectsById[id]?.parentId;
        current;
        current = objectsById[current]?.parentId
    ) {
        if (current === ancestorId) return true
    }
    return false
}

/** Whether `id` is locked, or sits inside a locked object — either way it has to stay put. */
export const isLockedOrInLocked = (objectsById: ObjectsByIdT, id: string | null | undefined) => {
    for (let current = id; current; current = objectsById[current]?.parentId) {
        if (objectsById[current]?.isLocked) return true
    }
    return false
}

/**
 * What a drag that starts on `draggedId` carries: the whole selection when the row is part of
 * it, otherwise the row alone — in tree order, without anything that already travels inside
 * another dragged object, and without locked objects (or ones inside a locked object).
 */
export const draggedObjectIds = (
    objectsById: ObjectsByIdT,
    draggedId: string,
    selectedIds: string[]
): string[] => {
    const ids = selectedIds.includes(draggedId) ? selectedIds : [draggedId]
    const movable = ids.filter((id) => objectsById[id] && !isLockedOrInLocked(objectsById, id))
    const roots = movable.filter(
        (id) => !movable.some((other) => other !== id && isDescendantOf(objectsById, id, other))
    )
    const order = treeOrder(objectsById)
    return roots.sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

/**
 * Whether the dragged objects may land at `placement` of `targetId`: not on or into one of
 * themselves, only inside an object that can hold children, and not into a locked object —
 * nothing could be dragged back out of it.
 */
export const canDrop = (
    objectsById: ObjectsByIdT,
    ids: string[],
    targetId: string,
    placement: DropPlacementT
): boolean => {
    const target = objectsById[targetId]
    if (!target || ids.length === 0) return false
    if (ids.includes(targetId)) return false
    if (ids.some((id) => isDescendantOf(objectsById, targetId, id))) return false
    const parentId = placement === "inside" ? target.id : target.parentId
    if (isLockedOrInLocked(objectsById, parentId)) return false
    if (placement === "inside") return PARENT_OBJECT_TYPES.includes(target.type)
    return true
}

/**
 * Where on a row the pointer drops: its top quarter before it, its bottom quarter after it,
 * the middle inside it — or, for a row that cannot hold children, its nearer half.
 *
 * @param offsetY the pointer's distance from the row's top
 */
export const dropPlacementAt = (
    offsetY: number,
    height: number,
    canNest: boolean
): DropPlacementT => {
    const share = height > 0 ? offsetY / height : 0
    if (!canNest) return share < 0.5 ? "before" : "after"
    if (share < EDGE_ZONE) return "before"
    if (share > 1 - EDGE_ZONE) return "after"
    return "inside"
}
